import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import api, { getApiErrorMessage } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { formatPoints } from "../../lib/display";
import { 
  ArrowLeft, 
  Save, 
  Plus, 
  Trash2, 
  GripVertical,
  ShoppingCart,
  Coins,
  Target,
  Calendar,
  Tags,
  Percent,
  Search,
  X
} from "lucide-react";

const ruleTypes = [
  { type: "valor_minimo", label: "Valor Mínimo", icon: ShoppingCart, color: "bg-blue-500" },
  { type: "produto_bonificado", label: "Produto Bonificado", icon: Tags, color: "bg-purple-500" },
  { type: "marca_bonificada", label: "Marca Bonificada", icon: Tags, color: "bg-fuchsia-500" },
  { type: "multiplicador", label: "Multiplicador", icon: Percent, color: "bg-green-500" },
  { type: "primeira_venda", label: "Primeira Venda", icon: Target, color: "bg-orange-500" },
];

const productBonusTypes = new Set(["item_bonificado", "produto_especifico", "produto_bonificado"]);

const getProductName = (produto) => (
  produto?.descricao_produto ||
  produto?.produto ||
  produto?.sku ||
  produto?.codigo_produto_erp ||
  "Produto sem descrição"
);

const getProductReference = (produto) => (
  produto?.codigo_produto_erp
    ? `Cód. ${produto.codigo_produto_erp}`
    : produto?.sku
      ? `SKU ${produto.sku}`
      : "Código não informado"
);

const CatalogSearch = ({ kind, selectedLabel, onSelect, onClear, onOpenChange }) => {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const isProduct = kind === "produto";

  useEffect(() => {
    if (!open) return undefined;

    let active = true;
    const timeoutId = window.setTimeout(async () => {
      setLoading(true);
      try {
        const response = await api.get(isProduct ? "/catalogo/produtos" : "/catalogo/marcas", {
          params: { busca: query, limite: 20 },
        });
        if (active) setOptions(response.data);
      } catch {
        if (active) setOptions([]);
      } finally {
        if (active) setLoading(false);
      }
    }, 220);

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [isProduct, open, query]);

  const handleSelect = (option) => {
    onSelect(option);
    setQuery("");
    setOpen(false);
    onOpenChange?.(false);
  };

  const placeholder = isProduct ? "Buscar produto pelo nome, SKU ou código" : "Buscar marca do ERP";
  const emptyMessage = query
    ? "Nenhum resultado encontrado no histórico ERP."
    : "Ainda não há produtos ou marcas ERP para selecionar.";

  return (
    <div className="space-y-2">
      {selectedLabel && (
        <div className="flex min-w-0 items-center justify-between gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2">
          <span className="min-w-0 truncate text-sm font-medium text-slate-800">{selectedLabel}</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 flex-shrink-0 text-slate-500 hover:bg-white hover:text-red-600"
            onClick={onClear}
            title="Limpar seleção"
            aria-label="Limpar seleção"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          value={query}
          onFocus={() => {
            setOpen(true);
            onOpenChange?.(true);
          }}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
            onOpenChange?.(true);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              setOpen(false);
              onOpenChange?.(false);
            }
          }}
          className="input-field h-9 pl-9"
          placeholder={placeholder}
          aria-label={placeholder}
        />
        {open && (
          <div className="absolute z-30 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white p-1 shadow-lg">
            {loading ? (
              <p className="px-3 py-3 text-sm text-slate-500">Buscando no catálogo ERP...</p>
            ) : options.length > 0 ? (
              options.map((option) => {
                const primaryText = isProduct ? getProductName(option) : option.nome_marca;
                const secondaryText = isProduct
                  ? [getProductReference(option), option.nome_marca].filter(Boolean).join(" · ")
                  : option.codigo_marca_erp ? `Cód. ${option.codigo_marca_erp}` : "Código não informado";
                return (
                  <button
                    key={isProduct ? `${option.codigo_produto_erp}-${option.sku}-${primaryText}` : `${option.codigo_marca_erp}-${option.nome_marca}`}
                    type="button"
                    className="block w-full rounded-md px-3 py-2 text-left hover:bg-slate-50 focus:bg-slate-50 focus:outline-none"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleSelect(option)}
                  >
                    <span className="block truncate text-sm font-medium text-slate-900">{primaryText}</span>
                    <span className="block truncate text-xs text-slate-500">{secondaryText}</span>
                  </button>
                );
              })
            ) : (
              <p className="px-3 py-3 text-sm text-slate-500">{emptyMessage}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const SortableRule = ({ rule, index, onUpdate, onRemove }) => {
  const [catalogOpen, setCatalogOpen] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rule.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const ruleConfig = ruleTypes.find(r => r.type === rule.type) ||
    (productBonusTypes.has(rule.type) ? ruleTypes.find(r => r.type === "produto_bonificado") : null);
  const Icon = ruleConfig?.icon || Coins;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative z-0 max-w-full overflow-visible rounded-xl border-2 border-slate-200 bg-white p-3 sm:p-4 ${catalogOpen ? "z-30" : ""} ${isDragging ? "shadow-xl" : ""}`}
    >
      <div className="flex min-w-0 items-start gap-3 sm:gap-4">
        <button
          {...attributes}
          {...listeners}
          className="mt-2 flex-shrink-0 text-slate-400 hover:text-slate-600 cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="h-5 w-5" />
        </button>

        <div className={`h-10 w-10 rounded-lg ${ruleConfig?.color || "bg-slate-500"} flex items-center justify-center flex-shrink-0`}>
          <Icon className="h-5 w-5 text-white" />
        </div>

        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex min-w-0 items-center justify-between gap-2">
            <h4 className="truncate font-medium text-slate-900">{ruleConfig?.label || "Regra"}</h4>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onRemove(index)}
              className="text-slate-400 hover:text-red-500"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          {rule.type === "valor_minimo" && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="min-w-0">
                <Label className="text-xs">Valor mínimo (R$)</Label>
                <Input
                  type="number"
                  value={rule.valor || ""}
                  onChange={(e) => onUpdate(index, { ...rule, valor: parseFloat(e.target.value) })}
                  className="input-field h-9"
                  placeholder="500"
                />
              </div>
              <div className="min-w-0">
                <Label className="text-xs">Pontos bônus</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={rule.pontos_bonus || ""}
                  onChange={(e) => onUpdate(index, { ...rule, pontos_bonus: parseFloat(e.target.value) })}
                  className="input-field h-9"
                  placeholder="100"
                />
              </div>
            </div>
          )}

          {productBonusTypes.has(rule.type) && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="min-w-0">
                <Label className="text-xs">Produto do catálogo ERP</Label>
                <CatalogSearch
                  kind="produto"
                  selectedLabel={rule.produto_nome || rule.produto || null}
                  onSelect={(produto) => onUpdate(index, {
                    ...rule,
                    produto: getProductName(produto),
                    produto_nome: getProductName(produto),
                    codigo_produto_erp: produto.codigo_produto_erp || "",
                    sku: produto.sku || "",
                  })}
                  onClear={() => onUpdate(index, {
                    ...rule,
                    produto: "",
                    produto_nome: "",
                    codigo_produto_erp: "",
                    sku: "",
                  })}
                  onOpenChange={setCatalogOpen}
                />
              </div>
              <div className="min-w-0">
                <Label className="text-xs">Pontos bônus por item</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={rule.pontos_bonus || ""}
                  onChange={(e) => onUpdate(index, { ...rule, pontos_bonus: parseFloat(e.target.value) })}
                  className="input-field h-9"
                  placeholder="50"
                />
              </div>
            </div>
          )}

          {rule.type === "marca_bonificada" && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="min-w-0">
                <Label className="text-xs">Marca do catálogo ERP</Label>
                <CatalogSearch
                  kind="marca"
                  selectedLabel={rule.nome_marca || rule.marca || null}
                  onSelect={(marca) => onUpdate(index, {
                    ...rule,
                    marca: marca.nome_marca,
                    nome_marca: marca.nome_marca,
                    codigo_marca_erp: marca.codigo_marca_erp || "",
                  })}
                  onClear={() => onUpdate(index, {
                    ...rule,
                    marca: "",
                    nome_marca: "",
                    codigo_marca_erp: "",
                  })}
                  onOpenChange={setCatalogOpen}
                />
              </div>
              <div className="min-w-0">
                <Label className="text-xs">Pontos bônus por item</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={rule.pontos_bonus || ""}
                  onChange={(e) => onUpdate(index, { ...rule, pontos_bonus: parseFloat(e.target.value) })}
                  className="input-field h-9"
                  placeholder="50"
                />
              </div>
            </div>
          )}

          {rule.type === "multiplicador" && (
            <div>
              <Label className="text-xs">Multiplicador de pontos</Label>
              <Input
                type="number"
                step="0.1"
                value={rule.multiplicador || ""}
                onChange={(e) => onUpdate(index, { ...rule, multiplicador: parseFloat(e.target.value) })}
                className="input-field h-9"
                placeholder="2.0"
              />
              <p className="text-xs text-slate-500 mt-1">Ex: 2.0 = pontos em dobro</p>
            </div>
          )}

          {rule.type === "primeira_venda" && (
            <div>
              <Label className="text-xs">Pontos bônus para primeira venda</Label>
              <Input
                type="number"
                step="0.01"
                value={rule.pontos_bonus || ""}
                onChange={(e) => onUpdate(index, { ...rule, pontos_bonus: parseFloat(e.target.value) })}
                className="input-field h-9"
                placeholder="200"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CampanhaEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campanha, setCampanha] = useState(null);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (id) {
      fetchCampanha();
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchCampanha = async () => {
    try {
      const response = await api.get(`/campanhas/${id}`);
      setCampanha(response.data);
      setRules(response.data.regras_json?.rules || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao carregar campanha."));
      navigate("/admin/campanhas");
    } finally {
      setLoading(false);
    }
  };

  const addRule = (type) => {
    const newRule = {
      id: `rule-${Date.now()}`,
      type,
    };
    setRules([...rules, newRule]);
  };

  const updateRule = (index, updatedRule) => {
    const newRules = [...rules];
    newRules[index] = updatedRule;
    setRules(newRules);
  };

  const removeRule = (index) => {
    setRules(rules.filter((_, i) => i !== index));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setRules((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSave = async () => {
    if (!id) {
      toast.error("Selecione uma campanha primeiro");
      return;
    }

    setSaving(true);
    try {
      await api.put(`/campanhas/${id}`, {
        ...campanha,
        regras_json: { rules },
      });
      toast.success("Regras salvas com sucesso!");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao salvar regras."));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-full space-y-6 overflow-hidden animate-fade-in" data-testid="campanha-editor">
      {/* Header */}
      <div className="flex min-w-0 flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex min-w-0 flex-col items-start gap-3 sm:flex-row sm:gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/admin/campanhas")}
            className="text-slate-600"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <div className="min-w-0">
            <h1 className="font-heading text-2xl font-bold text-slate-900 break-words">
              Editor de Regras
            </h1>
            {campanha && (
              <p className="break-words text-slate-600">{campanha.nome}</p>
            )}
          </div>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving || !id}
          className="btn-primary w-full sm:w-auto"
          data-testid="save-rules-btn"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? "Salvando..." : "Salvar Regras"}
        </Button>
      </div>

      {!id ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
          <p className="text-yellow-800">
            Selecione uma campanha existente na lista de campanhas para editar suas regras.
          </p>
          <Button
            variant="outline"
            onClick={() => navigate("/admin/campanhas")}
            className="mt-4"
          >
            Ver campanhas
          </Button>
        </div>
      ) : (
        <div className="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Rule palette */}
          <div className="min-w-0 lg:col-span-1">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 lg:sticky lg:top-6">
              <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">
                Adicionar Regra
              </h3>
              <p className="text-sm text-slate-500 mb-4">
                Clique para adicionar uma nova regra à campanha
              </p>
              <div className="space-y-2">
                {ruleTypes.map((rule) => (
                  <button
                    key={rule.type}
                    onClick={() => addRule(rule.type)}
                    className="flex w-full min-w-0 items-center gap-3 rounded-xl border-2 border-dashed border-slate-200 p-3 text-left transition-all hover:border-primary hover:bg-primary/5"
                    data-testid={`add-rule-${rule.type}`}
                  >
                    <div className={`h-10 w-10 rounded-lg ${rule.color} flex items-center justify-center`}>
                      <rule.icon className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate font-medium text-slate-900">{rule.label}</p>
                    </div>
                    <Plus className="ml-auto h-5 w-5 flex-shrink-0 text-slate-400" />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Rules list */}
          <div className="min-w-0 lg:col-span-2">
            <div className="min-h-[400px] rounded-2xl border border-slate-200 bg-slate-50 p-5 sm:p-6">
              <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">
                Regras da Campanha
              </h3>
              
              {rules.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                  <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                    <Coins className="h-8 w-8 text-slate-400" />
                  </div>
                  <p className="text-center">
                    Adicione regras para definir como os pontos serão calculados
                  </p>
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext items={rules} strategy={verticalListSortingStrategy}>
                    <div className="min-w-0 space-y-3">
                      {rules.map((rule, index) => (
                        <SortableRule
                          key={rule.id}
                          rule={rule}
                          index={index}
                          onUpdate={updateRule}
                          onRemove={removeRule}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>

            {/* Flow visualization */}
            {rules.length > 0 && (
              <div className="mt-6 max-w-full overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">
                  Fluxo de Cálculo
                </h3>
                <div className="flex flex-col items-center gap-2">
                  <div className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg font-medium">
                    Venda Registrada
                  </div>
                  <div className="h-8 w-0.5 bg-slate-300" />
                  {rules.map((rule, index) => {
                    const config = ruleTypes.find(r => r.type === rule.type);
                    return (
                      <div key={rule.id}>
                        <div className={`max-w-full break-words rounded-lg px-4 py-2 text-center text-sm font-medium text-white ${config?.color || "bg-slate-500"}`}>
                          {config?.label || "Regra"}
                          {rule.pontos_bonus && ` (+${formatPoints(rule.pontos_bonus)} pts)`}
                          {rule.multiplicador && ` (x${rule.multiplicador})`}
                        </div>
                        {index < rules.length - 1 && (
                          <div className="h-8 w-0.5 bg-slate-300 mx-auto" />
                        )}
                      </div>
                    );
                  })}
                  <div className="h-8 w-0.5 bg-slate-300" />
                  <div className="px-4 py-2 bg-green-100 text-green-700 rounded-lg font-medium">
                    Pontos Creditados
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CampanhaEditor;
